import { useEffect, useState } from 'react';
import { ABOUT } from '@/data/about';
import { initialsOf, TEAM, TEAM_GROUPS, teamIn, type TeamGroup, type TeamMember } from '@/data/team';
import { Reveal } from '@components/motion';
import FigmaFrame from '@components/landing/FigmaFrame';
import { Img } from '@/components/Img';

const Chevron = ({ dir }: { dir: 'left' | 'right' }) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
            d={dir === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

function NavArrows({ className, onPrev, onNext }: { className?: string; onPrev?: () => void; onNext?: () => void }) {
    return (
        <div className={`flex items-center gap-[24px] ${className ?? ''}`}>
            <button
                type="button"
                aria-label="Previous member"
                onClick={onPrev}
                className="flex size-[62px] items-center justify-center rounded-[70px] bg-black text-white transition-transform duration-300 hover:scale-[1.05]"
            >
                <Chevron dir="left" />
            </button>
            <button
                type="button"
                aria-label="Next member"
                onClick={onNext}
                className="flex size-[62px] items-center justify-center rounded-[70px] bg-white text-black shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-transform duration-300 hover:scale-[1.05]"
            >
                <Chevron dir="right" />
            </button>
        </div>
    );
}

/* Filmstrip geometry (design px). Cards share a bottom baseline; the active one
   grows tall while the rest sit as thumbnails, and the row slides so the active
   card always lands on the same anchor. */
const THUMB_W = 197;
const THUMB_H = 232;
const ACTIVE_W = 268;
const ACTIVE_H = 540;
const CARD_GAP = 24;
const ANCHOR = 860;
const EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';

/** A portrait, or the member's initials until one is uploaded. */
function Portrait({ member, className }: { member: TeamMember; className?: string }) {
    return member.photo ? (
        <Img src={member.photo} alt={member.name} className={`size-full object-cover object-top ${className ?? ''}`} />
    ) : (
        <span
            className={`flex size-full items-center justify-center bg-[#e6e6e6] font-primary text-[48px] font-semibold text-[#8a8a8a] ${className ?? ''}`}
        >
            {initialsOf(member.name)}
        </span>
    );
}

/** The group tabs — only the groups that have people, so an empty Board is never offered. */
function GroupTabs({
    groups,
    active,
    onSelect,
    className,
    style,
}: {
    groups: TeamGroup[];
    active: TeamGroup;
    onSelect: (g: TeamGroup) => void;
    className?: string;
    style?: React.CSSProperties;
}) {
    return (
        <div className={`flex items-center gap-[26px] ${className ?? ''}`} style={style}>
            {groups.map((g) => {
                const on = g === active;
                return (
                    <button
                        key={g}
                        type="button"
                        aria-pressed={on}
                        onClick={() => onSelect(g)}
                        className={`flex h-[50px] w-[188px] items-center justify-center text-[18px] font-semibold tracking-[-0.18px] ${
                            on ? 'bg-[#08080c] text-white' : 'border border-[#08080c] text-[#1b1b1b]'
                        }`}
                    >
                        {g}
                    </button>
                );
            })}
        </div>
    );
}

function TeamDesktop({
    roster,
    groups,
    group,
    onGroup,
}: {
    roster: TeamMember[];
    groups: TeamGroup[];
    group: TeamGroup;
    onGroup: (g: TeamGroup) => void;
}) {
    /** The roster repeated so the strip is always populated on both sides. */
    const STRIP = [...roster, ...roster, ...roster];
    // The strip repeats the roster three times and starts in the middle copy, so
    // there are always cards queued to the left — the row is full from the first
    // paint and never opens a gap as it slides.
    const [pos, setPos] = useState(roster.length);
    const [dir, setDir] = useState(1);
    const [animate, setAnimate] = useState(true);
    const index = ((pos % roster.length) + roster.length) % roster.length;
    const member = roster[index];

    const step = (delta: number) => {
        if (!delta) return;
        setDir(delta > 0 ? 1 : -1);
        setAnimate(true);
        setPos((p) => p + delta);
    };
    const go = (d: number) => step(d);
    const goTo = (target: number) => step(target - index);

    // Once a slide finishes outside the middle copy, jump back by one roster
    // length with transitions off — visually identical, but re-centred.
    useEffect(() => {
        if (pos >= roster.length && pos < roster.length * 2) return;
        const id = setTimeout(() => {
            setAnimate(false);
            setPos((p) => (p < roster.length ? p + roster.length : p - roster.length));
        }, 780);
        return () => clearTimeout(id);
    }, [pos, roster.length]);

    // Re-arm transitions on the frame after a silent re-centre.
    useEffect(() => {
        if (animate) return;
        const id = requestAnimationFrame(() => setAnimate(true));
        return () => cancelAnimationFrame(id);
    }, [animate]);

    // Slide the row so the active card always sits on the anchor.
    const rowShift = ANCHOR - pos * (THUMB_W + CARD_GAP);

    return (
        <FigmaFrame designWidth={1920} designHeight={1149} className="w-full">
            <div className="relative h-[1149px] w-[1920px] overflow-hidden bg-[#f5f5f5]">
                {/* Heading */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    className="absolute left-[140px] top-[120px] font-primary text-[190px] font-semibold leading-[1.01] tracking-[-9.5px] text-[#08080c]"
                >
                    <p className="mb-0">The</p>
                    <p>
                        Team<span className="text-[#f60]">.</span>
                    </p>
                </Reveal>

                {/* Intro paragraph */}
                <p className="absolute left-[678px] top-[300px] w-[283px] text-[18px] font-normal leading-[1.89] tracking-[-0.18px] text-[#050505]">
                    {ABOUT.team.intro}
                </p>

                {/* Tabs */}
                <GroupTabs
                    groups={groups}
                    active={group}
                    onSelect={onGroup}
                    className="absolute left-[140px] top-[596px]"
                />

                {/* Counter + vertical label */}
                <p className="absolute left-[140px] top-[700px] text-[16px] font-medium tracking-[0.04em] text-[#8a8a8a]">
                    {String(index + 1).padStart(2, '0')}{' '}
                    <span className="text-[#c4c4c4]">/ {String(roster.length).padStart(2, '0')}</span>
                </p>
                <p className="absolute left-[148px] top-[760px] rotate-180 text-[16px] font-semibold tracking-[0.12em] text-[#08080c] [writing-mode:vertical-rl]">
                    The Team
                </p>

                {/* Filmstrip — cards share a bottom baseline at y=1060 */}
                <div className="absolute bottom-[89px] left-0 h-[600px] w-[1160px] overflow-hidden">
                    <div
                        className="absolute bottom-0 flex items-end will-change-transform"
                        style={{
                            gap: CARD_GAP,
                            transform: `translateX(${rowShift}px)`,
                            transition: animate ? `transform 750ms ${EASE}` : 'none',
                        }}
                    >
                        {STRIP.map((m, i) => {
                            const active = i === pos;
                            return (
                                <button
                                    key={`${m.name}-${i}`}
                                    type="button"
                                    onClick={() => goTo(i % roster.length)}
                                    aria-label={`Show ${m.name}`}
                                    aria-pressed={active}
                                    tabIndex={i >= roster.length && i < roster.length * 2 ? 0 : -1}
                                    className="group relative shrink-0 overflow-hidden"
                                    style={{
                                        width: active ? ACTIVE_W : THUMB_W,
                                        height: active ? ACTIVE_H : THUMB_H,
                                        transition: animate ? `width 750ms ${EASE}, height 750ms ${EASE}` : 'none',
                                    }}
                                >
                                    <Portrait member={m} />
                                    {/* Inactive cards dim slightly until hovered */}
                                    <span
                                        className={`absolute inset-0 bg-white/30 transition-opacity duration-500 ${
                                            active ? 'opacity-0' : 'opacity-100 group-hover:opacity-0'
                                        }`}
                                    />
                                    {active && <span className="absolute inset-x-0 bottom-0 h-[6px] bg-[#f60]" />}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Member detail — cross-fades with the active card */}
                <div className="absolute left-[1180px] top-[540px] w-[600px]">
                    <div
                        key={index}
                        className="cg-sol-enter"
                        style={{ '--cg-sol-dir': `${dir * 40}px` } as React.CSSProperties}
                    >
                        <p className="text-[32px] leading-[1.4] tracking-[-0.32px] text-[#050505]">
                            {member.first} <span className="font-extrabold">{member.last}</span>
                        </p>
                        <p className="mt-[6px] text-[16px] leading-[1.6] tracking-[-0.16px] text-[#5e5e5e]">
                            {member.role}
                        </p>
                        {member.bio.length > 0 && (
                            <div className="mt-[20px] space-y-[18px] text-[16px] leading-[1.89] tracking-[-0.16px] text-[#050505]">
                                {member.bio.map((p, i) => (
                                    <p key={i}>{p}</p>
                                ))}
                            </div>
                        )}
                    </div>

                    <NavArrows className="mt-[36px]" onPrev={() => go(-1)} onNext={() => go(1)} />
                </div>
            </div>
        </FigmaFrame>
    );
}

function TeamMobile({
    roster,
    groups,
    group,
    onGroup,
}: {
    roster: TeamMember[];
    groups: TeamGroup[];
    group: TeamGroup;
    onGroup: (g: TeamGroup) => void;
}) {
    const [index, setIndex] = useState(0);
    const [dir, setDir] = useState(1);
    const member = roster[index];

    const go = (step: number) => {
        setDir(step);
        setIndex((i) => (i + step + roster.length) % roster.length);
    };
    const goTo = (target: number) => {
        if (target === index) return;
        setDir(target > index ? 1 : -1);
        setIndex(target);
    };

    return (
        <section className="bg-[#f5f5f5] px-5 py-16">
            <h2 className="font-primary text-[64px] font-semibold leading-[1.01] tracking-[-3px] text-[#08080c]">
                The
                <br />
                Team<span className="text-[#f60]">.</span>
            </h2>
            <p className="mt-5 text-[15px] font-normal leading-[1.7] text-[#050505]">{ABOUT.team.intro}</p>
            <GroupTabs groups={groups} active={group} onSelect={onGroup} className="mt-6 flex-wrap gap-3" />

            <div
                key={index}
                className="cg-sol-enter"
                style={{ '--cg-sol-dir': `${dir * 36}px` } as React.CSSProperties}
            >
                <div className="mt-8 h-[420px] overflow-hidden rounded-[12px]">
                    <Portrait member={member} />
                </div>
                <p className="mt-5 text-[24px] text-[#050505]">
                    {member.first} <span className="font-extrabold">{member.last}</span>
                </p>
                <p className="text-[14px] text-[#5e5e5e]">{member.role}</p>
                {member.bio.length > 0 && (
                    <div className="mt-4 space-y-4 text-[14px] leading-[1.8] text-[#050505]">
                        {member.bio.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </div>
                )}
            </div>

            <NavArrows className="mt-6" onPrev={() => go(-1)} onNext={() => go(1)} />

            <div className="scrollbar-hide -mx-5 mt-6 flex gap-4 overflow-x-auto px-5 pb-2">
                {roster.map((m, i) => (
                    <button key={m.name} type="button" onClick={() => goTo(i)} className="w-[150px] shrink-0">
                        <div className="h-[176px] w-[150px] overflow-hidden">
                            <Portrait member={m} className="text-[32px]" />
                        </div>
                        <div className={`h-[5px] w-full ${i === index ? 'bg-[#f60]' : 'bg-transparent'}`} />
                    </button>
                ))}
            </div>
        </section>
    );
}

/**
 * Read at render so the hydrated roster is what shows. Only groups with people
 * are offered; the first of them opens. Keyed by group so the carousels start
 * afresh when the tab changes.
 */
export default function AboutTeam() {
    const groups = TEAM_GROUPS.filter((g) => teamIn(g).length > 0);
    const [chosen, setChosen] = useState<TeamGroup | null>(null);
    const group = chosen && groups.includes(chosen) ? chosen : groups[0];
    if (!group) return null;
    const roster = teamIn(group);
    if (TEAM.length === 0) return null;
    return (
        <>
            <div className="hidden lg:block">
                <TeamDesktop key={group} roster={roster} groups={groups} group={group} onGroup={setChosen} />
            </div>
            <div className="lg:hidden">
                <TeamMobile key={group} roster={roster} groups={groups} group={group} onGroup={setChosen} />
            </div>
        </>
    );
}
